import { ConvergeAgreementApi } from '@/api/convergeAgreement';
import type { AgreementListData, QueryAgreementParams } from '@/api/convergeAgreement/types';
import { buildSubmitAgreementParams, formatAgreementList } from '@/shared/agreement';
import { ubtSendError } from '@/shared/utils/ubt';
import { ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import store from '@/store';
import { agreementsPopService } from './agreements-pop-service';

interface ConvergeAgreementsCache {
  agreementListData: AgreementListData | null;
  isConfirmed: boolean;
  /** 是否正在加载协议数据 */
  isLoading: boolean;
  /** 是否存在征信协议 */
  hasCreditRouteChannelAgreement: boolean;
}

/**
 * 协议确认同意服务（使用聚合协议接口）
 */
class ConfirmConvergeAgreementsService {
  private agreementsCache: ConvergeAgreementsCache = {
    agreementListData: null,
    isConfirmed: false,
    isLoading: false,
    hasCreditRouteChannelAgreement: false,
  };

  private initAgreementsTaskCache: Promise<AgreementListData> | null = null;

  /**
   * 获取 extraData 透传参数
   * @returns extraData
   */
  private getExtraData(): Record<string, unknown> | undefined {
    return store.state.base.businessInfoExtra?.userInfo?.extraData;
  }

  /**
   * 查询协议列表数据
   * @returns 协议列表数据
   */
  private async queryAgreementsData(): Promise<AgreementListData | null> {
    try {
      const data = await ConvergeAgreementApi.queryAgreement({
        extraData: this.getExtraData(),
      });

      return data;
    } catch (error) {
      ubtSendError({
        name: 'queryAgreementsData-error',
        message: (error as Error)?.message,
      });
      return null;
    }
  }

  private updateAgreementsCache(agreementListData: AgreementListData) {
    const {
      registerAgreement = { list: [], show: false },
      userRegisterAgreement = { list: [], show: false },
      creditAgreement = { list: [], show: false },
      creditRouteChannelAgreement = { list: [], show: false },
      loading,
    } = agreementListData;
    const hasCreditRouteChannelAgreement = !!(
      creditRouteChannelAgreement?.show && creditRouteChannelAgreement?.list?.length
    );
    /** 判断是否存在未签署的协议（需要同时满足 show=true 且 list有数据） */
    const needSignAgreements = !!((registerAgreement?.show && registerAgreement?.list?.length)
      || (userRegisterAgreement?.show && userRegisterAgreement?.list?.length)
      || (creditAgreement?.show && creditAgreement?.list?.length)
      || hasCreditRouteChannelAgreement);

    this.agreementsCache.agreementListData = agreementListData;
    this.agreementsCache.isConfirmed = !needSignAgreements;
    this.agreementsCache.isLoading = loading;
    this.agreementsCache.hasCreditRouteChannelAgreement = hasCreditRouteChannelAgreement;
  }

  /**
   * 初始化协议数据
   */
  private async initAgreementsData(): Promise<AgreementListData> {
    this.initAgreementsTaskCache = new Promise(async (resolve, reject) => {
      try {
        const agreementListData = await this.queryAgreementsData();

        /** 若协议数据异常，则返回null，并触发reject */
        if (agreementListData) {
          this.updateAgreementsCache(agreementListData);

          if (!this.agreementsCache.isConfirmed && this.agreementsCache.isLoading) {
            // 轮询查询协议数据直到 loading 不为 true，并更新弹窗数据
            const pollAgreementsData = async (): Promise<void> => {
              const pollData = await this.queryAgreementsData();

              if (pollData) {
                // 更新缓存数据
                this.updateAgreementsCache(pollData);

                const { isLoading, hasCreditRouteChannelAgreement } = this.agreementsCache;

                // 更新弹窗数据（如果弹窗已打开）
                agreementsPopService.updateParams({
                  agreementList: formatAgreementList(pollData),
                  isLoading,
                  hasCreditRouteChannelAgreement,
                });

                // 如果还在加载中，继续轮询
                if (pollData.loading) {
                  setTimeout(() => {
                    pollAgreementsData();
                  }, 1000);
                }
              }
            };

            // 延迟1秒后开始第一次轮询
            setTimeout(() => {
              pollAgreementsData();
            }, 1000);
          }

          resolve(agreementListData);
        } else {
          reject();
        }
      } catch (error) {
        /** 服务接口异常，清除缓存 */
        this.initAgreementsTaskCache = null;
        reject(error);
      }
    });

    return this.initAgreementsTaskCache;
  }

  /**
   * 预加载协议数据（对外使用，异常已进行try-catch兜底）
   */
  public async preloadAgreementsData(): Promise<void> {
    try {
      /** 清空缓存数据，避免更新不及时问题 */
      this.initAgreementsTaskCache = null;

      await this.initAgreementsData();
    } catch (error) {
      ubtSendError({
        name: 'preloadConvergeAgreementsData error',
        message: (error as Error)?.message,
      });
    }
  }

  /**
   * 确认并同意协议
   * @param params 查询协议参数
   * @returns {Promise<boolean>} 是否同意协议成功
   */
  public async confirmConvergeAgreements(
    params?: QueryAgreementParams,
  ): Promise<boolean> {
    try {
      /** 埋点上报 */
      ubtSetupPageButton('finance_agreeConvergeAgreements');

      /** 初始化协议数据 */
      const agreementListData = await this.initAgreementsData();

      /** 若已同意过协议，则直接返回true */
      if (this.agreementsCache.isConfirmed) return true;

      try {
        await agreementsPopService.open({
          agreementList: formatAgreementList(agreementListData),
          isLoading: this.agreementsCache.isLoading,
          hasCreditRouteChannelAgreement: this.agreementsCache.hasCreditRouteChannelAgreement,
        });
      } catch (error) {
        /** 用户取消或关闭弹窗，返回false，中断主流程 */
        return false;
      }

      /** 提交保存-同意协议 */
      const submitParams = buildSubmitAgreementParams(
        agreementListData,
        params?.transactionId || '',
      );

      await ConvergeAgreementApi.submitAgreement({
        ...submitParams,
        extraData: this.getExtraData(),
      });

      return true;
    } catch (error) {
      ubtSendError(error as Error);
      /** 因服务接口异常，不阻塞主流程 */
      return true;
    }
  }
}

/** 聚合协议服务实例 */
export const convergeAgreementService = new ConfirmConvergeAgreementsService();
